import {Alert, Button, Card, Checkbox, Col, Form, Input, notification, Row, Select} from "antd";
import React, {useEffect} from "react";
import api, {getApiErrorData} from "../../api";
import './style.css';
import {ArrowRightOutlined} from "@ant-design/icons";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useTranslations} from "../../lang";
import {setUtmData} from "../../helpers";
import {getInitialRegistrationCountry, getRegistrationMarketProfile, HARDINESS_ZONES, parseRegistrationPlanId} from "./market";

export default function Register() {

    const [notificationApi, contextHolder] = notification.useNotification();
    const {translate, language} = useTranslations()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    let selectedPlanId;
    let invalidPlanId = false;
    try {
        selectedPlanId = parseRegistrationPlanId(searchParams.get('plan_id'));
    } catch (error) {
        invalidPlanId = true;
    }
    const requestedMarket = searchParams.get('market') === 'US' ? 'US' : 'EU'
    const [currentStep, setCurrentStep] = React.useState(1);
    const [form] = Form.useForm();
    const selectedCountry = Form.useWatch('country', form);
    const marketProfile = getRegistrationMarketProfile(selectedCountry, language);

    useEffect(() => {
        if (api.isLoggedIn()) {
            navigate("/gardens");
        }
    }, []);


    // Get UTM data from localStorage
    const getUtmData = () => {
        try {
            const utmData = localStorage.getItem('utm_data');
            if (utmData) {
                const parsed = JSON.parse(utmData);
                // Check if data is less than 30 days old (30 * 24 * 60 * 60 * 1000 ms)
                const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
                if (parsed.timestamp > thirtyDaysAgo) {
                    return parsed;
                }
                // Remove expired UTM data
                localStorage.removeItem('utm_data');
            }
        } catch (error) {
            console.error('Error parsing UTM data from localStorage:', error);
            localStorage.removeItem('utm_data');
        }
        return null;
    };

    const onStep1Continue = async () => {
        try {
            // Validate only step 1 fields
            await form.validateFields(['name', 'email', 'password', 'password_confirm', 'tos']);
            setCurrentStep(2);
        } catch (error) {
            // Validation failed, errors will be shown by form
            console.log('Step 1 validation failed:', error);
        }
    };

    const onFinish = (values) => {
        const utmData = getUtmData();

        api.register(
            values.email,
            values.name,
            values.password,
            values.company,
            utmData?.utm_source || null,
            utmData?.utm_medium || null,
            utmData?.utm_campaign || null,
            values.country,
            marketProfile.language,
            marketProfile.measurements,
            utmData?.supplier_ids || null,
            values.hardiness_zone || null,
            selectedPlanId
        ).then((response) => {
            if (response.token) {
                localStorage.setItem('token', response.token);
                // Clean up UTM data after successful registration
                localStorage.removeItem('utm_data');
                window.location.href = response.url || "/welcome";
            }
        }).catch((error) => {
            if (getApiErrorData(error).detail === "UserAlreadyExists") {
                notificationApi.error({
                    message: translate(`Dit emailadres is al in gebruik!`),
                    description: translate('Probeer in te loggen met dit emailadres of gebruik een ander emailadres.'),
                    placement: 'top',
                    duration: 3
                });
                return
            }

            notificationApi.error({
                message: translate(`Woops! Er is iets misgegaan! 🥕`),
                description: translate('Check of je alles goed hebt ingevuld en probeer het later nog eens. Lukt het nog steeds niet? Neem contact op met ons.'),
                placement: 'top',
                duration: 3
            });
        })
    }


    setUtmData();

    if (invalidPlanId) {
        return (
            <div style={{maxWidth: '720px', margin: '24px auto'}}>
                <Alert
                    type="error"
                    showIcon
                    message={translate('Dit abonnement is niet beschikbaar')}
                    description={translate('Controleer de registratielink en kies opnieuw een abonnement.')}
                />
            </div>
        );
    }

    return (
        <>
            <Form
                form={form}
                name="register_form"
                className="register-form"
                initialValues={{
                    remember: true,
                    country: getInitialRegistrationCountry(requestedMarket),
                }}
                onFinish={onFinish}>
                <Row justify="center">
                    {contextHolder}
                    <Col md={12} xs={24}>
                        <Card style={{backgroundColor: 'white', marginBottom: '24px'}}>
                            <div style={{display: currentStep === 1 ? 'block' : 'none'}}>
                                <h1>{translate('Registreer vandaag nog!')}</h1>
                                <p>{translate('Begin vandaag nog met het plannen van jouw moestuin.')}</p>

                                <h2>{translate('Over jou')}</h2>
                                <Form.Item
                                    name="name"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Vul je voor en achternaam in'),
                                        },
                                    ]}
                                >
                                    <Input placeholder={translate('Voor- en achternaam')}/>
                                </Form.Item>
                                <Form.Item
                                    name="email"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Vul een emailadres in'),
                                        },
                                        {
                                            type: 'email',
                                            message: translate('Vul een geldig emailadres in'),
                                        },
                                    ]}
                                >
                                    <Input placeholder={translate('E-mail address')}/>
                                </Form.Item>
                                <h2>{translate('Voor je account')}</h2>
                                <Form.Item
                                    name="password"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Voer een wachtwoord in!'),
                                        },
                                    ]}
                                >
                                    <Input.Password placeholder={translate('Wachtwoord')}/>
                                </Form.Item>
                                <Form.Item
                                    name="password_confirm"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Voer een wachtwoord in!'),
                                        },
                                        ({getFieldValue}) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error(translate('De wachtwoorden die je hebt ingevuld komen niet overeen!')));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password placeholder={translate('Herhaal wachtwoord')}/>
                                </Form.Item>

                                <Form.Item name={"tos"} valuePropName="checked" rules={[{
                                    required: true,
                                    message: translate('Je moet akkoord gaan met de algemene voorwaarden en privacy policy!')
                                }]}>
                                    <Checkbox>
                                        {translate('Ik ga akkoord met de ')} <a href="https://www.oogst.app/tos"
                                                                                target={"_blank"}>{translate('algemene voorwaarden')}</a> {translate('en')}
                                        <a target={"_blank"}
                                           href="https://www.oogst.app/privacy-policy">{translate('privacy policy')}</a>.</Checkbox>
                                </Form.Item>
                            </div>

                            <div style={{display: currentStep === 2 ? 'block' : 'none'}}>
                                <h1>{translate('Over jouw tuin')}</h1>
                                <p>{translate('Vertel ons meer over je tuin om te beginnen.')}</p>

                                <Form.Item
                                    name="company"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Vul de naam van je tuin in, bijvoorbeeld: "Stef\'s moestuin" of "Tuinderij de brocoli"'),
                                        },
                                    ]}>
                                    <Input placeholder={translate('Naam van je tuin(derij)')}/>
                                </Form.Item>
                                <Form.Item
                                    name="country"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Selecteer je land'),
                                        },
                                    ]}>
                                    <Select placeholder={translate('Selecteer je land')}>
                                        <Select.Option value="nl">🇳🇱 {translate('Nederland')}</Select.Option>
                                        <Select.Option value="be">🇧🇪 {translate('België')}</Select.Option>
                                        <Select.Option value="de">🇩🇪 {translate('Duitsland')}</Select.Option>
                                        <Select.Option value="us">🇺🇸 {translate('Verenigde Staten')}</Select.Option>
                                        {/*<Select.Option value="fr">🇫🇷 {translate('Frankrijk')}</Select.Option>*/}
                                        {/*<Select.Option value="gb">🇬🇧 {translate('Verenigd Koninkrijk')}</Select.Option>*/}
                                        {/*<Select.Option value="ca">🇨🇦 {translate('Canada')}</Select.Option>*/}
                                        {/*<Select.Option value="au">🇦🇺 {translate('Australië')}</Select.Option>*/}
                                        {/*<Select.Option value="other">{translate('Anders')}</Select.Option>*/}
                                    </Select>
                                </Form.Item>
                                {marketProfile.requiresHardinessZone && (
                                    <Form.Item
                                        name="hardiness_zone"
                                        label={translate('USDA winterhardheidszone')}
                                        rules={[{
                                            required: true,
                                            message: translate('Selecteer je USDA winterhardheidszone'),
                                        }]}
                                    >
                                        <Select placeholder={translate('Selecteer je USDA winterhardheidszone')}>
                                            {HARDINESS_ZONES.map((zone) => (
                                                <Select.Option key={zone} value={zone}>{zone}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>
                <Row>
                    <Col span={24} className={"text-center"}>
                        {currentStep === 1 && (
                            <Form.Item>
                                <Button type="primary" size={"large"} onClick={onStep1Continue} className="login-form-button">
                                    {translate('Volgende')} <ArrowRightOutlined/>
                                </Button>
                            </Form.Item>
                        )}
                        {currentStep === 2 && (
                            <>
                                <Form.Item style={{display: 'inline-block', marginRight: '12px'}}>
                                    <Button size={"large"} onClick={() => setCurrentStep(1)}>
                                        {translate('Terug')}
                                    </Button>
                                </Form.Item>
                                <Form.Item style={{display: 'inline-block'}}>
                                    <Button type="primary" size={"large"} htmlType="submit" className="login-form-button">
                                        {translate('Aanmelden en direct beginnen')} <ArrowRightOutlined/>
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Col>
                </Row>

                <Row>
                    <Col span={24} className={"text-center"}>
                        <Button type={"link"}
                                onClick={() => navigate('/login')}>{translate('Terug naar inloggen')}</Button>
                    </Col>
                </Row>
            </Form>
        </>
    );
}